import { Injectable, inject } from '@angular/core';
import { MandatoryFormatActivitiesDocument, FormatMandatoryActivitiesEntry, MandatoryInlineActivityRule } from '@omni/interfaces/meeting/mandatory-format-activities.interface';
import { AuthenticationService } from '@omni/services/authentication.service';
import { IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';

/** When Dynamics does not return `BU_APPOINTMENT_GA_FORMAT_MANDATORY_INLINE_ACTIVITIES`, no per-format rules apply. */
const EMPTY_MANDATORY_FORMAT_ACTIVITIES_DOC: MandatoryFormatActivitiesDocument = { formats: [] };

/**
 * Per-format mandatory inline-activity rules and optional date/time field visibility (OMNI-59151 / 59152, OMNI-59496).
 * Precedence: `setDocumentFromJson` (tests) → `user.ioConfigurations` BU
 * `BU_APPOINTMENT_GA_FORMAT_MANDATORY_INLINE_ACTIVITIES` (JSON) → empty document (no app-side bundled defaults).
 */
@Injectable({ providedIn: 'root' })
export class MeetingMandatoryActivitiesConfigService {
  private readonly auth = inject(AuthenticationService);
  private explicit: MandatoryFormatActivitiesDocument | null = null;
  /** Parsed JSON from user IO; invalidated when `configValue` changes (e.g. delta sync updates BU IO). */
  private fromUser: MandatoryFormatActivitiesDocument | null = null;
  /** Last raw `configValue` string used to build `fromUser`, so we re-parse after sync without caching stale JSON forever. */
  private lastUserIoConfigValueRaw: string | undefined;

  getDocument(): MandatoryFormatActivitiesDocument {
    if (this.explicit) {
      return this.explicit;
    }
    const row = this.auth.user?.ioConfigurations?.find(
      c => c.featureCode === IoConfigurationFeatureCodes.BU_APPOINTMENT_GA_FORMAT_MANDATORY_INLINE_ACTIVITIES
    );
    const cv = row?.configValue ?? '';
    if (this.fromUser != null && this.lastUserIoConfigValueRaw === cv) {
      return this.fromUser;
    }
    this.lastUserIoConfigValueRaw = cv;
    if (!cv) {
      this.fromUser = null;
      return EMPTY_MANDATORY_FORMAT_ACTIVITIES_DOC;
    }
    this.fromUser = this.parseMandatoryFormatActivitiesJson(cv);
    return this.fromUser ?? EMPTY_MANDATORY_FORMAT_ACTIVITIES_DOC;
  }

  private parseMandatoryFormatActivitiesJson(raw: string): MandatoryFormatActivitiesDocument | null {
    const s = (raw || '').trim();
    if (!s) {
      return null;
    }
    const tryParse = (value: string) => JSON.parse(value) as MandatoryFormatActivitiesDocument;
    try {
      return tryParse(s);
    } catch {
      try {
        // Dynamics / manual entry may include trailing commas; JSON does not allow them
        return tryParse(s.replace(/,(\s*[\]\}])/g, '$1'));
      } catch {
        return null;
      }
    }
  }

  /** Pass `null` to clear explicit JSON and re-read from user IO on next `getDocument`. */
  setDocumentFromJson(json: string | MandatoryFormatActivitiesDocument | null | undefined): void {
    if (json == null) {
      this.explicit = null;
      this.fromUser = null;
      this.lastUserIoConfigValueRaw = undefined;
      return;
    }
    if (typeof json === 'string') {
      const parsed = this.parseMandatoryFormatActivitiesJson(json);
      this.explicit = parsed ?? EMPTY_MANDATORY_FORMAT_ACTIVITIES_DOC;
    } else {
      this.explicit = json;
    }
  }

  getConfigForFormatId(formatId: string | null | undefined): FormatMandatoryActivitiesEntry | null {
    if (!formatId) {
      return null;
    }
    const doc = this.getDocument();
    if (!doc?.formats?.length) {
      return null;
    }
    return doc.formats.find(f => f.formatId === formatId) ?? null;
  }

  getMandatoryRulesForFormat(formatId: string | null | undefined): MandatoryInlineActivityRule[] {
    return this.getConfigForFormatId(formatId)?.mandatoryInlineActivities ?? [];
  }

  /**
   * Returns `true` when the selected format explicitly hides an inline activity section via
   * `hideInlineActivities`, provided the same activity type is NOT also listed in
   * `mandatoryInlineActivities` (mandatory rules always take precedence).
   *
   * @param formatId  The meeting's `indskr_activitytype` value.
   * @param activityType  e.g. `'SampleDrop'`
   */
  shouldHideInlineActivityForFormat(formatId: string | null | undefined, activityType: string): boolean {
    const entry = this.getConfigForFormatId(formatId);
    if (!entry) {
      return false;
    }
    const isMandatory = (entry.mandatoryInlineActivities ?? []).some(r => r.activityType === activityType);
    if (isMandatory) {
      return false;
    }
    return (entry.hideInlineActivities ?? []).includes(activityType);
  }

  /**
   * Whether start/end date & time fields should be hidden on the meeting details screen.
   * If the format has `hideDateTimeFields` in IO JSON, that wins; else falls back to
   * BU `BU_APPOINTMENT_GA_HIDE_DATE_FIELDS`. If there is no format id, BU flag only.
   */
  shouldHideDateTimeFieldsForFormat(formatId: string | null | undefined): boolean {
    if (!formatId) {
      return this.auth.hasIoConfigWithFeatureCode(
        IoConfigurationFeatureCodes.BU_APPOINTMENT_GA_HIDE_DATE_FIELDS
      ) as boolean;
    }
    const entry = this.getConfigForFormatId(formatId);
    if (entry && typeof entry.hideDateTimeFields === 'boolean') {
      return entry.hideDateTimeFields;
    }
    return this.auth.hasIoConfigWithFeatureCode(
      IoConfigurationFeatureCodes.BU_APPOINTMENT_GA_HIDE_DATE_FIELDS
    ) as boolean;
  }
}
